import { useEffect, useRef, useState, type ReactNode } from "react";
import type { Comment, LikeStatus } from "../api/types";
import { fmtRelative } from "./format";

/**
 * Likes and comments, over anything that can be liked and commented on.
 *
 * <p>Extracted from `LikeCommentBar` when the Forum needed the same thing for a
 * Post. The alternative was a second copy of the optimistic like flip, the
 * lazy comment load and the compose form — three pieces of state that are easy
 * to get subtly wrong and would then be wrong in only one of two places.
 *
 * <p>What stayed behind in `LikeCommentBar` is the species Tag… picker, which
 * is meaningless for a Post. It comes back in through {@link Props.extra}.
 */
export interface SocialTarget {
  /** Stable identity of the thing being liked — refetches when it changes. */
  key: string;
  getLikes: () => Promise<LikeStatus>;
  setLiked: (liked: boolean) => Promise<LikeStatus>;
  listComments: () => Promise<Comment[]>;
  addComment: (body: string) => Promise<Comment>;
  /**
   * Optional, because only the Forum supports them — an Image's comments have no
   * edit or delete endpoint. Absent means the controls are never offered, so
   * capability comes from the adapter and ownership from the row's `canEdit`;
   * both have to agree before a button appears.
   */
  editComment?: (commentId: number, body: string) => Promise<Comment>;
  deleteComment?: (commentId: number) => Promise<void>;
}

interface Props {
  target: SocialTarget;
  /** "compact" sits inside a card, "full" opens with the comments already shown. */
  variant: "compact" | "full";
  /**
   * A count the server already knows, used until the panel is opened. The feed
   * ships one per Post, which is what lets the comment button show a number
   * without loading every thread on the page.
   */
  initialCommentCount?: number;
  /** Extra controls in the actions row — the image bar's Tag… picker. */
  extra?: ReactNode;
}

export function LikesAndComments({ target, variant, initialCommentCount, extra }: Props) {
  const [likes, setLikes] = useState<LikeStatus | null>(null);
  const [comments, setComments] = useState<Comment[] | null>(null);
  const [open, setOpen] = useState(variant === "full");
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [likeBusy, setLikeBusy] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Fetch likes immediately so the heart count is correct on render. Comments
  // are heavier and only loaded once the user opens the panel — keeps the
  // grid query budget small.
  useEffect(() => {
    let cancelled = false;
    target.getLikes().then(
      (s) => !cancelled && setLikes(s),
      () => !cancelled && setLikes({ count: 0, likedByMe: false }),
    );
    return () => {
      cancelled = true;
    };
    // Keyed on identity, not on the target object: an adapter rebuilt every
    // render would otherwise refetch on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.key]);

  useEffect(() => {
    if (!open || comments !== null) return;
    let cancelled = false;
    target.listComments().then(
      (cs) => !cancelled && setComments(cs),
      () => !cancelled && setComments([]),
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, comments, target.key]);

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  const onLike = async (e: React.MouseEvent) => {
    stop(e);
    if (likeBusy || !likes) return;
    setLikeBusy(true);
    const next = !likes.likedByMe;
    // Optimistic flip — server response replaces state on settle.
    setLikes({
      count: likes.count + (next ? 1 : -1),
      likedByMe: next,
    });
    try {
      const updated = await target.setLiked(next);
      setLikes(updated);
    } catch {
      setLikes(likes); // rollback
    } finally {
      setLikeBusy(false);
    }
  };

  const onToggleComments = (e: React.MouseEvent) => {
    stop(e);
    setOpen((v) => !v);
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const text = draft.trim();
    if (!text || posting) return;
    setPosting(true);
    try {
      const created = await target.addComment(text);
      setComments((prev) => [...(prev ?? []), created]);
      setDraft("");
      inputRef.current?.focus();
    } finally {
      setPosting(false);
    }
  };

  const likeCount = likes?.count ?? 0;
  const likedByMe = likes?.likedByMe ?? false;
  // Once loaded, the list is the truth; before that, the server's count is
  // better than an empty button.
  const commentCount = comments?.length ?? initialCommentCount ?? null;

  return (
    <div className={`social social-${variant}`} onClick={stop}>
      <div className="social-actions">
        <button
          type="button"
          className={`social-btn social-like${likedByMe ? " on" : ""}`}
          onClick={onLike}
          disabled={likes === null || likeBusy}
          aria-pressed={likedByMe}
          aria-label={likedByMe ? "Unlike" : "Like"}
          title={likedByMe ? "Unlike" : "Like"}
        >
          <HeartIcon filled={likedByMe} />
          <span className="social-count">{likeCount}</span>
        </button>
        <button
          type="button"
          className={`social-btn social-comment${open ? " on" : ""}`}
          onClick={onToggleComments}
          aria-pressed={open}
          aria-label={open ? "Hide comments" : "Show comments"}
          title={open ? "Hide comments" : "Show comments"}
        >
          <CommentIcon />
          <span className="social-count">{commentCount ?? ""}</span>
        </button>
        {extra}
      </div>

      {open && (
        <div className="social-panel" onClick={stop}>
          {comments === null ? (
            <div className="social-empty">Loading…</div>
          ) : comments.length === 0 ? (
            <div className="social-empty">No comments yet.</div>
          ) : (
            <ul className="social-comments">
              {comments.map((c) => (
                <CommentRow
                  key={c.commentId}
                  comment={c}
                  canChange={Boolean(c.canEdit && target.editComment && target.deleteComment)}
                  onEdited={(saved) =>
                    setComments((prev) =>
                      (prev ?? []).map((x) => (x.commentId === saved.commentId ? saved : x)),
                    )
                  }
                  onDeleted={(id) =>
                    setComments((prev) => (prev ?? []).filter((x) => x.commentId !== id))
                  }
                  edit={target.editComment}
                  remove={target.deleteComment}
                />
              ))}
            </ul>
          )}
          <form className="social-compose" onSubmit={onSubmit}>
            <input
              ref={inputRef}
              type="text"
              className="social-input"
              placeholder="Write a comment…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onClick={stop}
              maxLength={2000}
              disabled={posting}
            />
            <button
              type="submit"
              className="social-post"
              disabled={posting || draft.trim().length === 0}
            >
              {posting ? "…" : "Post"}
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

/**
 * One comment, with in-place edit and delete when it is yours.
 *
 * <p>Editing swaps the text for an input in the same row rather than opening a
 * dialog: a comment is one line, and a modal for one line puts more chrome on
 * screen than content.
 *
 * <p>Delete asks first, inline. Two clicks for something irreversible, without a
 * browser confirm() — those block the page and read as an error.
 */
function CommentRow({
  comment,
  canChange,
  edit,
  remove,
  onEdited,
  onDeleted,
}: {
  comment: Comment;
  /** Both that the API supports changing it and that this one is yours. */
  canChange: boolean;
  edit?: (commentId: number, body: string) => Promise<Comment>;
  remove?: (commentId: number) => Promise<void>;
  onEdited: (saved: Comment) => void;
  onDeleted: (commentId: number) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(comment.body);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();
  const edited = comment.updatedAt !== comment.createdAt;

  async function save(e: React.FormEvent) {
    e.preventDefault();
    stop(e);
    const text = draft.trim();
    // Emptying is a deletion in disguise; the server refuses it too. Offer the
    // real button rather than an error.
    if (!text || busy || !edit) return;
    if (text === comment.body) {
      setEditing(false);
      return;
    }
    setBusy(true);
    setFailed(false);
    try {
      onEdited(await edit(comment.commentId, text));
      setEditing(false);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  async function confirmRemove(e: React.MouseEvent) {
    stop(e);
    if (busy || !remove) return;
    setBusy(true);
    setFailed(false);
    try {
      await remove(comment.commentId);
      onDeleted(comment.commentId);
    } catch {
      setFailed(true);
      setBusy(false);
    }
  }

  if (editing) {
    return (
      <li className="social-comment-row social-comment-editing">
        <form className="social-compose" onSubmit={save}>
          <input
            type="text"
            className="social-input"
            value={draft}
            autoFocus
            maxLength={2000}
            disabled={busy}
            onClick={stop}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setDraft(comment.body);
                setEditing(false);
              }
            }}
          />
          <button
            type="submit"
            className="social-post"
            disabled={busy || draft.trim().length === 0}
          >
            {busy ? "…" : "Save"}
          </button>
          <button
            type="button"
            className="social-comment-action"
            onClick={(e) => {
              stop(e);
              setDraft(comment.body);
              setEditing(false);
            }}
          >
            Cancel
          </button>
        </form>
        {failed && <span className="social-comment-failed">Could not save that.</span>}
      </li>
    );
  }

  return (
    <li className="social-comment-row">
      <span className="social-author">{comment.username}</span>
      <span className="social-body">{comment.body}</span>
      <span className="social-time">
        {fmtRelative(new Date(comment.createdAt))}
        {edited && (
          <span
            className="social-comment-edited"
            title={`Edited ${new Date(comment.updatedAt).toLocaleString()}`}
          >
            {" "}
            (edited)
          </span>
        )}
      </span>
      {canChange && !confirming && (
        <span className="social-comment-actions">
          <button
            type="button"
            className="social-comment-action"
            onClick={(e) => {
              stop(e);
              setDraft(comment.body);
              setEditing(true);
            }}
          >
            Edit
          </button>
          <button
            type="button"
            className="social-comment-action social-comment-danger"
            onClick={(e) => {
              stop(e);
              setConfirming(true);
            }}
          >
            Delete
          </button>
        </span>
      )}
      {canChange && confirming && (
        <span className="social-comment-actions">
          <span className="social-comment-ask">Delete this?</span>
          <button
            type="button"
            className="social-comment-action"
            onClick={(e) => {
              stop(e);
              setConfirming(false);
            }}
          >
            Keep
          </button>
          <button
            type="button"
            className="social-comment-action social-comment-danger"
            disabled={busy}
            onClick={confirmRemove}
          >
            {busy ? "…" : "Delete"}
          </button>
        </span>
      )}
      {failed && <span className="social-comment-failed">That didn’t work.</span>}
    </li>
  );
}

export function HeartIcon({ filled }: { filled: boolean }) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        d="M12 21s-7-4.35-9.5-9A5.5 5.5 0 0 1 12 6a5.5 5.5 0 0 1 9.5 6c-2.5 4.65-9.5 9-9.5 9z"
        fill={filled ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function CommentIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
      <path
        d="M4 5h16a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H9l-4 4v-4H4a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
    </svg>
  );
}
